import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ProjectService from "../../../services/ProjectService";
import { useAuth } from "../../../context/AuthContext";
import { useNotification } from "../../../context/NotificationContext";

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const uniqueMessages = (messages) => Array.from(new Set(messages.filter(Boolean)));

const ShareProjectDialog = ({
    open,
    projectId,
    accessUsers,
    onClose,
    onShared,
}) => {
    const { user } = useAuth();
    const showNotification = useNotification();
    const [emails, setEmails] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [validationMessages, setValidationMessages] = useState([]);
    const [isSharing, setIsSharing] = useState(false);

    const existingAccessEmails = useMemo(
        () =>
            new Set(
                (accessUsers || [])
                    .map((item) => normalizeEmail(item.email))
                    .filter(Boolean),
            ),
        [accessUsers],
    );

    const currentUserEmail = normalizeEmail(user?.email);

    useEffect(() => {
        if (!open) {
            setEmails([]);
            setInputValue("");
            setValidationMessages([]);
            setIsSharing(false);
        }
    }, [open]);

    const validateEmailCandidate = (email, nextEmails) => {
        const messages = [];

        if (!isValidEmail(email)) {
            messages.push("Enter a valid email address.");
        }

        if (nextEmails.includes(email)) {
            messages.push("This email has already been added.");
        }

        if (email && email === currentUserEmail) {
            messages.push("You cannot share a project with yourself.");
        }

        if (existingAccessEmails.has(email)) {
            messages.push("This user already has access to the project.");
        }

        return messages;
    };

    const buildEmailState = (rawValue, baseEmails = emails) => {
        const tokens = String(rawValue || "")
            .split(",")
            .map(normalizeEmail)
            .filter(Boolean);
        const nextEmails = [...baseEmails];
        const messages = [];

        tokens.forEach((email) => {
            const candidateMessages = validateEmailCandidate(email, nextEmails);
            if (candidateMessages.length) {
                messages.push(...candidateMessages);
                return;
            }

            nextEmails.push(email);
        });

        return {
            nextEmails,
            messages: uniqueMessages(messages),
        };
    };

    const addEmailsFromInput = () => {
        if (!inputValue.trim()) return;

        const { nextEmails, messages } = buildEmailState(inputValue);
        setEmails(nextEmails);
        setValidationMessages(messages);
        setInputValue("");
    };

    const handleInputKeyDown = (event) => {
        if (["Enter", ",", "Tab"].includes(event.key) && inputValue.trim()) {
            event.preventDefault();
            addEmailsFromInput();
        }
    };

    const handleDeleteEmail = (emailToDelete) => {
        setEmails((prevEmails) =>
            prevEmails.filter((email) => email !== emailToDelete),
        );
    };

    const handleShare = async () => {
        const { nextEmails, messages } = inputValue.trim()
            ? buildEmailState(inputValue)
            : { nextEmails: emails, messages: [] };

        if (messages.length) {
            setEmails(nextEmails);
            setValidationMessages(messages);
            setInputValue("");
            return;
        }

        if (!nextEmails.length) {
            return;
        }

        try {
            setIsSharing(true);
            const response = await ProjectService.shareProject(projectId, nextEmails);
            onShared(response.users || []);
            showNotification(
                "Project shared successfully. You can change access in the Project Settings.",
                "success",
            );
            onClose();
        } catch (error) {
            const responseErrors = error?.response?.data?.errors;
            const errorMessage = error?.response?.data?.error;
            setValidationMessages(
                Array.isArray(responseErrors)
                    ? responseErrors
                    : uniqueMessages(
                          String(errorMessage || "Failed to share project.").split("\n"),
                      ),
            );
        } finally {
            setIsSharing(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Share project</DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 1 }}>
                    Invite people to collaborate on this project.
                </Typography>
                <Typography sx={{ mb: 3 }}>
                    Everyone you invite will have full access to view, edit and
                    run tests.
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 1,
                    }}
                >
                    {emails.map((email) => (
                        <Chip
                            key={email}
                            label={email}
                            onDelete={() => handleDeleteEmail(email)}
                        />
                    ))}
                </Box>
                <TextField
                    fullWidth
                    value={inputValue}
                    onChange={(event) => {
                        setInputValue(event.target.value);
                        if (validationMessages.length) {
                            setValidationMessages([]);
                        }
                    }}
                    onKeyDown={handleInputKeyDown}
                    placeholder="Please enter email addresses"
                    error={Boolean(validationMessages.length)}
                    helperText={validationMessages.join("\n")}
                    FormHelperTextProps={{ sx: { whiteSpace: "pre-line" } }}
                    autoFocus
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <LoadingButton
                    loading={isSharing}
                    variant="contained"
                    color="secondary"
                    onClick={handleShare}
                    disabled={!emails.length}
                >
                    Share project
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ShareProjectDialog;
